// BarChart.js
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import moment from 'moment';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Drawer, Menu, Collapse, Button, Switch, Spinner, H5, Icon, Text } from '@blueprintjs/core';
import {
  MapContainer,
  TileLayer,
  WMSTileLayer,
  Marker,
  Popup,
  LayersControl,
  LayerGroup,
  Polygon,
  ZoomControl,
  GeoJSON,
  Pane
} from 'react-leaflet';
import { Pannellum } from 'pannellum-react';
import { dateFormat } from '../common/dateFormat';
import mapIcon from '../icons/map.svg';
import cameraIcon from '../icons/camera.svg';
import { ItemContext, StatusContext } from '../App';
import {
  enumKeyByValue,
  GoogleItemType,
  partPromaItemTypeTranslations,
  posPartPromaItemTypeTranslations,
  PromaBlocks,
  promaChildType,
  PromaItemType,
  promaItemTypeTranslations
} from './itemDefinitions';
import getPromaHierachy, { getChildren } from './getPromaHierarchy';
import googleFileLoader from '../google/GFileLoader';
import darkClassNames from '../common/darkClassNames';
import { RiskFilterActions } from '../filters/filterReducer';
import { photos, hotSpots } from './data';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow
});

const MapIcon = L.icon({
  iconUrl: mapIcon,
  iconSize: new L.Point(20, 20)
});

const CameraIcon = L.icon({
  iconUrl: cameraIcon,
  iconSize: new L.Point(20, 20)
});

L.Marker.prototype.options.icon = DefaultIcon;

export const itemDueClass = (item) =>
  item.status === 'Valmis'
    ? 'disabled'
    : moment(item.endDate, dateFormat).isBefore(moment(new Date()))
    ? 'danger'
    : moment(item.endDate, dateFormat).isBefore(moment(new Date()).add(3, 'months'))
    ? 'primary'
    : '';

const itemIsActive = (item) =>
  item.status === 'Valmis'
    ? false
    : moment(item.startDate, dateFormat).isBefore(moment(new Date())) ||
      item.status === 'Käynnissä';

const itemIsLate = (item) =>
  item.status === 'Valmis' ? false : moment(item.endDate, dateFormat).isBefore(moment(new Date()));

const viewTimeIsToday = (viewTime) => moment(new Date()).isSame(moment(viewTime), 'day');

const LateIndicator = ({ item }) => {
  return itemIsLate(item) ? (
    <div>
      <Icon icon="warning-sign" intent="danger" />
      <Text className="dst-inline" style={{ color: '#A82A2A', paddingLeft: '5px' }}>
        Myöhässä!
      </Text>
    </div>
  ) : (
    ''
  );
};

const itemIsActiveOn = (item, viewTime) =>
  moment(item.startDate, dateFormat).isBefore(moment(viewTime)) &&
  moment(item.endDate, dateFormat).isAfter(moment(viewTime));

const itemIsFinishedOn = (item, viewTime) =>
  moment(item.endDate, dateFormat).isBefore(moment(viewTime));

const startDateWithError = (startDate) => startDate ?? 'Ei aloituspäivämäärää';

const PromaCollapseList = ({ item, onlyChildren }) => {
  const [isOpen, setOpen] = useState(false);
  const updateOpen = (e) => {
    setOpen(!isOpen);
  };

  const hasChildren = item.children && item.children.length > 0;

  return (
    <div>
      <Button
        onClick={updateOpen}
        minimal
        small
        icon={hasChildren ? (isOpen ? 'chevron-up' : 'chevron-down') : ''}
      >
        {onlyChildren ? (
          `${item.children.length} ${
            posPartPromaItemTypeTranslations[promaChildType[item.itemType]]
          }`
        ) : (
          <div className={itemDueClass(item)}>{item.endDate + ' ' + item.name}</div>
        )}
      </Button>
      {hasChildren ? (
        <Collapse isOpen={isOpen}>
          <ul>
            {item.children.map((child) => (
              <li style={{ marginLeft: '15px' }} key={child.id} className={itemDueClass(child)}>
                {' ' + child.endDate + ' ' + child.name}
              </li>
            ))}
          </ul>
        </Collapse>
      ) : (
        ''
      )}
    </div>
  );
};

const toTitleCase = (str) =>
  str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });

export const namesFromEmails = (mailList) =>
  mailList
    ? mailList.reduce((str, item) => {
        const namestrings = item.match(/^.+(?=@)/)[0].split('.');
        return `${str} ${toTitleCase(
          namestrings.reduce((nameString, partName) => `${nameString} ${partName}`, '')
        )}`;
      }, '')
    : '';

export const lightItemColor = (item, viewTime) =>
  itemIsLate(item) && viewTimeIsToday(viewTime)
    ? '#DB3737'
    : itemIsActiveOn(item, viewTime) || (itemIsActive(item) && viewTimeIsToday(viewTime))
    ? '#137CBD'
    : itemIsFinishedOn(item, viewTime)
    ? '#15B371'
    : '#738694';

export const darkItemColor = (item, viewTime) =>
  itemIsLate(item) && viewTimeIsToday(viewTime)
    ? '#A82A2A'
    : itemIsActiveOn(item, viewTime)
    ? '#0E5A8A'
    : itemIsFinishedOn(item, viewTime)
    ? '#0A6640'
    : '#738694';

export const PromaPopupContent = ({
  item,
  filteredItems,
  hideChildren,
  noChildren,
  blockInTitle
}) => {
  let children = [];
  if (!noChildren) {
    children = getChildren(item, filteredItems)[0];
  }
  const ChildInfo = () => {
    if (noChildren) {
      return '';
    }
    if (children.length < 1) {
      return partPromaItemTypeTranslations[promaChildType[item.itemType]] ?? '';
    }
    if (hideChildren) {
      return `${item.children.length} ${
        posPartPromaItemTypeTranslations[promaChildType[item.itemType]]
      }`;
    }
    return <PromaCollapseList item={{ ...item, children }} onlyChildren />;
  };

  const titleText = blockInTitle ? `${item.name} (${item.block})` : `${item.name}`;

  return (
    <div>
      <H5>{titleText}</H5>
      <LateIndicator item={item} />
      <p>
        <span style={{ color: '#738694' }}>
          {item.numericId ? `${promaItemTypeTranslations[item.itemType]} ${item.numericId}` : ''}{' '}
          {`${item.status}`}
        </span>
        <br />
        {`${startDateWithError(item.startDate)} - ${item.endDate}`}
        <br />
        {`Vastuu: ${namesFromEmails(item.owners)}`}
      </p>
      <ChildInfo />
    </div>
  );
};

export const GetPromaItem = ({ jsonitem, uuid, filteredItems, viewTime }) => {
  if (filteredItems && filteredItems.length > 0) {
    const item = filteredItems.find((item) => item.id === uuid);
    // console.log(item);
    if (item !== undefined) {
      const stroke = !itemIsFinishedOn(item, viewTime);
      return (
        <GeoJSON
          data={jsonitem}
          key={item.name}
          pathOptions={{ color: darkItemColor(item, viewTime), stroke }}
        >
          <Popup>
            <PromaPopupContent item={item} filteredItems={filteredItems} />
          </Popup>
        </GeoJSON>
      );
    }
    return `UUID ${uuid} not recognized`;
  }
  return uuid;
};

const GetHierarchyByBlock = ({ block, showItems }) => {
  const [hierarchy, setHierarchy] = useState();
  const { itemProps } = useContext(ItemContext);

  useEffect(() => {
    getPromaHierachy({ showItems, itemProps, setTaskHierarchy: setHierarchy });
  }, [itemProps, showItems]);

  if (hierarchy && hierarchy.children) {
    const returnString = (
      <div style={{ width: '450px' }}>
        <div>
          <h2>{block}</h2>
          <b>Päätavoitteet:</b>
          {hierarchy.children
            .filter((child) => child.block === block)
            .map((child) => (
              <PromaCollapseList key={child.id} item={child} />
            ))}
        </div>
        <br />
        <div>
          <b>Käynnissä olevat tuotokset:</b>
          {hierarchy.children
            .filter((child) => child.block === block && child.children)
            .reduce((list, item) => list.concat(item.children), [])
            .filter((item) => item.children)
            .reduce((list, item) => list.concat(item.children), [])
            .filter((item) => itemIsActive(item))
            .sort((a, b) =>
              moment(a.endDate, dateFormat).diff(moment(b.endDate, dateFormat), 'days')
            )
            .map((child) => (
              <li key={child.id} className={itemDueClass(child)}>
                {child.endDate + ' ' + child.name}
              </li>
            ))}
        </div>
      </div>
    );
    return returnString;
  }
  return block;
};

const processCoords = (item) => {
  if (item.polygon && item.polygon.length > 0) {
    if (item.inverseCoords) {
      return item.polygon.map((coords) => [coords[1], coords[0]]);
    }
    return item.polygon;
  }
  return [];
};

const printProps = (item) => {
  const propList = [];
  for (const [key, value] of Object.entries(item.properties)) {
    if (value && !['color', 'type', 'fillOpacity', 'stroke'].includes(key)) {
      propList.push(
        <p>
          {key}: {value}
        </p>
      );
    }
  }
  return propList;
};

const collectionPropsToChild = (collection) => {
  const newCollection = collection;
  const updateFeature = (feature) => {
    const newFeature = feature;
    for (const [key, value] of Object.entries(collection.properties)) {
      if (!feature.properties[key]) {
        newFeature.properties[key] = value;
      }
    }
    return newFeature;
  };
  newCollection.features = collection.features.map((feature) => updateFeature(feature));
  return newCollection;
};

const MapItems = ({ defaultLayers, zoom }) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [width, setWidth] = useState(window.innerWidth);
  const [altPhotos, setAltPhotos] = useState(false);
  const { darkMode } = useContext(StatusContext);

  const [drawerPhoto, setDrawerPhoto] = useState();

  const ref = useRef();

  const photoItems = photos;
  const hotSpotItems = hotSpots;

  const photoItemsWithMultiInfo = (items) => {
    const locItems = items.map((item) => {
      const hasSameLocs =
        items.filter(
          (pItem) =>
            pItem.imageMediaMetadata.location.latitude ===
              item.imageMediaMetadata.location.latitude &&
            pItem.imageMediaMetadata.location.longitude ===
              item.imageMediaMetadata.location.longitude
        ).length > 1;
      return { ...item, hasSameLocs };
    });
    return locItems;
  };

  const processedModelPhotos = photoItemsWithMultiInfo(
    photoItems.filter((item) => item.source === 'Virtuaalimalli')
  );

  /** Update the size of the graph element - with a short delay to avoid too many renders */
  const updateGraphSize = () => {
    setTimeout(() => {
      if (height !== window.innerHeight) {
        setHeight(window.innerHeight);
      }
      if (width !== window.innerWidth) {
        setWidth(window.innerWidth);
      }
    }, 200);
  };

  window.addEventListener('resize', updateGraphSize);

  return (
    <div style={{ width, height }} id="mapid">
      <Drawer
        className={darkClassNames(darkMode)}
        isOpen={drawerPhoto}
        onClose={() => {
          setDrawerPhoto();
        }}
        icon="camera"
        title={drawerPhoto ? <>{`${drawerPhoto.title}`}</> : ''}
        size="100%"
      >
        {drawerPhoto ? (
          <Pannellum
            width="100%"
            height="100%"
            image={altPhotos ? drawerPhoto.photoAlt : drawerPhoto.photo}
            onError={altPhotos ? undefined : () => setAltPhotos(true)}
            // onLoad={() => ref.current.getViewer().toggleFullscreen()}
            autoLoad
            showFullscreenCtrl
            ref={ref}
          >
            {hotSpotItems
              .filter((item) => item.fileName === drawerPhoto.name)
              .map((item) => (
                <Pannellum.HotSpot key={item.key} {...item} />
              ))}
          </Pannellum>
        ) : (
          ''
        )}
      </Drawer>
      <MapContainer
        center={[60.19091, 24.95498]}
        zoom={zoom}
        maxZoom={20}
        scrollWheelZoom
        className="bp3-running-text"
        zoomControl={false}
      >
        <ZoomControl position="bottomleft" />
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked key="Helsinki Ninja Bright" name="Helsinki Ninja Bright">
            <TileLayer
              attribution='&copy; Helsingin kaupunki, <a href="https://www.hel.fi/helsinki/fi/kartat-ja-liikenne/kartat-ja-paikkatieto/">kaupunkiympäristön toimialan kaupunkimittauspalvelut</a>, <a href="http://creativecommons.org/licenses/by/4.0">CC BY 4.0</a>.'
              url="https://tiles.hel.ninja/styles/hel-osm-bright/{z}/{x}/{y}@2x@fi.png"
              maxZoom={21}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer key="Helsinki Ninja Light" name="Helsinki Ninja Light">
            <TileLayer
              attribution='&copy; Helsingin kaupunki, <a href="https://www.hel.fi/helsinki/fi/kartat-ja-liikenne/kartat-ja-paikkatieto/">kaupunkiympäristön toimialan kaupunkimittauspalvelut</a>, <a href="http://creativecommons.org/licenses/by/4.0">CC BY 4.0</a>.'
              url="https://tiles.hel.ninja/styles/hel-osm-light/{z}/{x}/{y}@2x@fi.png"
              maxZoom={21}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer key="HSL Map" name="HSL Map">
            <TileLayer
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://cdn.digitransit.fi/map/v1/hsl-map/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer key="Ortoilmakuva 2021 5cm" name="Ortoilmakuva 2021 5cm">
            <WMSTileLayer
              attribution='&copy; Helsingin kaupunki, <a href="https://www.hel.fi/helsinki/fi/kartat-ja-liikenne/kartat-ja-paikkatieto/">kaupunkiympäristön toimialan kaupunkimittauspalvelut</a>, <a href="http://creativecommons.org/licenses/by/4.0">CC BY 4.0</a>.'
              layers="avoindata:Ortoilmakuva_2021_5cm"
              url="https://kartta.hel.fi/ws/geoserver/avoindata/ows"
              maxZoom={21}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer key="Stamen watercolour" name="Stamen watercolour">
            <TileLayer
              attribution='Map tiles by <a href="http://stamen.com">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a>. Data by <a href="http://openstreetmap.org">OpenStreetMap</a>, under <a href="http://creativecommons.org/licenses/by-sa/3.0">CC BY SA</a>.'
              url="https://stamen-tiles.a.ssl.fastly.net/watercolor/{z}/{x}/{y}.jpg"
            />
          </LayersControl.BaseLayer>
          <LayersControl.Overlay
            checked={defaultLayers.includes('Virtuaalimalli')}
            key="Virtuaalimalli"
            name={`<img src=${cameraIcon} height="15" width="15"/> Virtuaalimalli`}
          >
            <LayerGroup>
              {processedModelPhotos.map((item) => (
                <Marker
                  key={item.id}
                  position={[
                    item.imageMediaMetadata.location.latitude,
                    item.imageMediaMetadata.location.longitude
                  ]}
                  icon={CameraIcon}
                  eventHandlers={{ click: () => (item.hasSameLocs ? '' : setDrawerPhoto(item)) }}
                >
                  {item.hasSameLocs ? (
                    <Popup maxWidth="700px">
                      <Menu>
                        {processedModelPhotos
                          .filter(
                            (pItem) =>
                              pItem.imageMediaMetadata.location.latitude ===
                                item.imageMediaMetadata.location.latitude &&
                              pItem.imageMediaMetadata.location.longitude ===
                                item.imageMediaMetadata.location.longitude
                          )
                          .map((fItem) => (
                            <Menu.Item
                              key={fItem.id}
                              icon="camera"
                              onClick={() => {
                                setDrawerPhoto(fItem);
                              }}
                              text={fItem.name}
                              title={fItem.name}
                            />
                          ))}
                      </Menu>
                    </Popup>
                  ) : (
                    ''
                  )}
                </Marker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
      </MapContainer>
    </div>
  );
};

export default MapItems;
